import { apiFetch, ApiError } from '@/shared/lib/apiClient';
import type {
  CodeLoginDto,
  CodeStatusResponseDto,
  RestoreParticipantResponse,
  UserResponseDto,
} from '@/shared/lib/apiTypes';

/**
 * 다른 디바이스에서 발급받은 유저 코드로 진행 상태를 복원한다.
 *
 * Swagger 명세상 두 단계 호출을 합성한다:
 *   1. `POST /api/login/code` — 코드 존재 여부 확인 (서버는 같은 코드를 에코)
 *   2. `GET /api/code/status/{userCode}` — 누적 카운트와 다운로드 URL 조회
 *
 * 서버는 누적된 코드 목록을 반환하지 않으므로, 복원 결과는 카운트와 다운로드 URL만 담긴다.
 *
 * @param code - 사용자가 입력한 유저 코드 (6자리 정규화된 형태).
 * @returns 복원된 유저 코드, 누적 카운트, 보상 다운로드 URL.
 * @throws ApiError(400) 일치하는 유저 코드가 없는 경우.
 * @throws ApiError(404) status 조회 단계에서 유저를 찾을 수 없는 경우.
 */
export async function restoreParticipant(
  code: string,
): Promise<RestoreParticipantResponse> {
  // 1단계: 로그인 검증 — 코드가 서버에 존재하는지 확인하고 정규화된 형태를 받아온다.
  const loginBody: CodeLoginDto = { code };
  const loginResp = await apiFetch<UserResponseDto>('/api/login/code', {
    method: 'POST',
    body: loginBody,
  });
  const userCode = loginResp.userCode;

  // 2단계: 진행 상태 조회 — 1단계가 성공했으므로 사실상 항상 성공해야 한다.
  // 실패 시에는 서버가 보낸 메시지(예: "유저를 찾을 수 없습니다.")를 그대로 노출하고,
  // 코드 라벨만 PARTICIPANT_NOT_FOUND로 마킹해 호출 측이 분기할 수 있게 한다 (정책 P2=B).
  let status: CodeStatusResponseDto;
  try {
    status = await apiFetch<CodeStatusResponseDto>(
      `/api/code/status/${encodeURIComponent(userCode)}`,
      { method: 'GET' },
    );
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 404) {
      throw new ApiError(
        cause.message,
        404,
        cause.code ?? 'PARTICIPANT_NOT_FOUND',
        cause.payload,
      );
    }
    throw cause;
  }

  return {
    code: userCode,
    codeCount: status.codeCount,
    downloadUrl: status.downloadUrl,
  };
}
